import { useCallback } from 'react';
import { useLang } from './lang';

/**
 * Bilingual server-error messages for the appointments app.
 *
 * Every message the user can be shown must exist in both languages, but the
 * text of a rejected request originates on the server — so the API sends a
 * stable machine code and we translate it here, next to the rest of the UI
 * strings. Two envelope shapes are in play:
 *
 *   appointments routes → { error: 'SLOT_CONFLICT', message: '…', params?: {…} }
 *   global errorHandler → { error: 'Validation failed', code: 'VALIDATION_ERROR' }
 *
 * so `extractError` looks for the code in both slots. Messages carrying a value
 * (an invoice number, an item id) are functions over `params`, which the API
 * sends alongside the code — that avoids parsing the English sentence to
 * recover the number.
 *
 * An unmapped code falls back to a generic translated sentence with the raw
 * code appended, so support can still act on it without showing English prose
 * to an Arabic user.
 */

export interface Msg {
  en: string;
  ar: string;
}

type Params = Record<string, string | number>;
type Entry = Msg | ((p: Params) => Msg);

/** Appointment domain rejections — thrown as AppointmentError by the API. */
const APPOINTMENT_ERRORS: Record<string, Entry> = {
  SLOT_CONFLICT: {
    en: 'The employee already has an appointment in this time slot.',
    ar: 'لدى الموظف موعد آخر في هذه الفترة الزمنية.',
  },
  CAPACITY_REACHED: {
    en: 'The employee has reached the daily appointment limit.',
    ar: 'وصل الموظف إلى الحد الأقصى للمواعيد اليومية.',
  },
  INVALID_TIME: {
    en: 'End time must be after start time.',
    ar: 'يجب أن يكون وقت الانتهاء بعد وقت البداية.',
  },
  NOT_FOUND: {
    en: 'Appointment not found.',
    ar: 'الموعد غير موجود.',
  },
  INVALID_BRANCH: {
    en: 'Branch does not belong to this subsidiary.',
    ar: 'هذا الفرع لا ينتمي إلى الشركة الحالية.',
  },
  INVALID_SERVICE: (p) => (p.item_id != null
    ? { en: `Service ${p.item_id} is not available.`, ar: `الخدمة رقم ${p.item_id} غير متاحة.` }
    : { en: 'That service is not available.', ar: 'هذه الخدمة غير متاحة.' }),
  INVALID_CUSTOMER: {
    en: 'Customer not found.',
    ar: 'العميل غير موجود.',
  },
  CUSTOMER_REQUIRED: {
    en: 'A customer (id or name) is required.',
    ar: 'يجب تحديد عميل (بالاختيار من القائمة أو بكتابة الاسم).',
  },
  NO_STATUS: {
    en: 'No appointment statuses configured.',
    ar: 'لم يتم إعداد حالات المواعيد.',
  },
  INVALID_STATUS: {
    en: 'Target status not found.',
    ar: 'الحالة المطلوبة غير موجودة.',
  },
  TRANSITION_NOT_ALLOWED: {
    en: 'This status change is not permitted by the workflow.',
    ar: 'تغيير الحالة هذا غير مسموح به في مسار العمل.',
  },
  ALREADY_CONVERTED: (p) => (p.invoice != null
    ? {
        en: `Appointment already converted to sale invoice #${p.invoice}.`,
        ar: `تم تحويل هذا الموعد بالفعل إلى فاتورة مبيعات رقم ${p.invoice}.`,
      }
    : {
        en: 'Appointment already converted to a sale invoice.',
        ar: 'تم تحويل هذا الموعد بالفعل إلى فاتورة مبيعات.',
      }),
  NO_SERVICES: {
    en: 'Cannot convert an appointment with no services.',
    ar: 'لا يمكن تحويل موعد لا يحتوي على أي خدمات.',
  },
  // ── Card payment / Tap ───────────────────────────────────────────────────
  TAP_NOT_CONFIGURED: {
    en: 'Tap is not switched on for this company. Enable it in Admin → Payment gateways.',
    ar: 'خدمة Tap غير مفعّلة لهذه الشركة. فعّلها من الإدارة ← بوابات الدفع.',
  },
  TAP_NOT_AVAILABLE: {
    en: 'Card payment for appointments is not available on this company yet.',
    ar: 'الدفع بالبطاقة للمواعيد غير متاح لهذه الشركة بعد.',
  },
  TAP_CHARGE_FAILED: (p) => (p.reason != null
    ? {
        en: `Tap refused the payment: ${p.reason}`,
        ar: `رفضت Tap عملية الدفع: ${p.reason}`,
      }
    : {
        en: 'Could not start the card payment. Please try again.',
        ar: 'تعذّر بدء عملية الدفع بالبطاقة. يرجى المحاولة مرة أخرى.',
      }),
  NOTHING_TO_CHARGE: {
    en: 'The deposit already covers this invoice — convert it without taking another payment.',
    ar: 'العربون يغطي هذه الفاتورة بالكامل — حوّلها دون استلام دفعة أخرى.',
  },
  CUSTOMER_PHONE_REQUIRED: {
    en: "The customer's mobile number is required so the payment link can be sent by SMS.",
    ar: 'رقم جوال العميل مطلوب لإرسال رابط الدفع عبر رسالة نصية.',
  },
  // ── Reception-collected deposits (migration 058) ─────────────────────────
  PAYMENTS_NOT_AVAILABLE: {
    en: 'Collecting deposits is not available on this company yet (database migration 058 pending).',
    ar: 'تحصيل العربون غير متاح لهذه الشركة بعد (بانتظار تحديث قاعدة البيانات 058).',
  },
  SESSION_CANCELLED: {
    en: 'This appointment is cancelled; no payment can be taken on it.',
    ar: 'هذا الموعد ملغى؛ لا يمكن استلام أي دفعة عليه.',
  },
  DEPOSIT_NOT_POSITIVE: {
    en: 'The deposit must be more than zero.',
    ar: 'يجب أن يكون العربون أكبر من صفر.',
  },
  DEPOSIT_EXCEEDS_REMAINING: (p) => ({
    en: `The deposit cannot exceed the remaining balance${p.remaining != null ? ` of ${p.remaining}` : ''}.`,
    ar: `لا يمكن أن يتجاوز العربون المبلغ المتبقي${p.remaining != null ? ` (${p.remaining})` : ''}.`,
  }),
  DEPOSIT_PENDING_CONFLICT: (p) => ({
    en: `A payment link${p.pending != null ? ` for ${p.pending}` : ''} is still pending. Cancel it or wait for it before asking for more.`,
    ar: `هناك رابط دفع${p.pending != null ? ` بقيمة ${p.pending}` : ''} لا يزال معلّقًا. ألغِه أو انتظر سداده قبل طلب مبلغ آخر.`,
  }),
  PAYMENT_TYPE_NOT_FOUND: {
    en: 'That payment method does not exist for this company.',
    ar: 'طريقة الدفع هذه غير موجودة لهذه الشركة.',
  },
  PAYMENT_TYPE_NOT_ALLOWED: {
    en: 'A deposit must be taken on a real payment method (a link can only be sent through Tap).',
    ar: 'يجب استلام العربون بطريقة دفع فعلية (يُرسل الرابط عبر Tap فقط).',
  },
  PAYMENT_NOT_FOUND: {
    en: 'Payment not found.',
    ar: 'الدفعة غير موجودة.',
  },
  PAYMENT_NOT_PENDING: {
    en: 'Only a pending payment can be changed.',
    ar: 'يمكن تعديل الدفعة المعلّقة فقط.',
  },
  PAYMENT_ALREADY_PAID: {
    en: 'The customer has just paid this link; it can no longer be cancelled.',
    ar: 'قام العميل بسداد هذا الرابط للتو؛ لم يعد بالإمكان إلغاؤه.',
  },
  DEPOSIT_PAYMENT_TYPE_MISSING: {
    en: 'The payment method a deposit was taken on no longer exists for this company.',
    ar: 'طريقة الدفع التي استُلم بها العربون لم تعد موجودة لهذه الشركة.',
  },
  DEPOSIT_ON_ACCOUNT_CONFLICT: {
    en: 'A deposit was already paid, so the balance cannot be left unpaid or booked on credit.',
    ar: 'تم دفع عربون بالفعل، لذا لا يمكن ترك المتبقي غير مدفوع أو تسجيله على الحساب.',
  },
};

/**
 * Rejections raised by the ORDER service while an appointment is being
 * converted into a sale. They arrive through the global error handler
 * (`{ error: <prose>, code: <CODE> }`) rather than the appointments envelope,
 * and they are the ones a receptionist can actually act on at the counter —
 * a mistyped split, an unpaid receipt with nobody to owe it.
 */
const ORDER_ERRORS: Record<string, Entry> = {
  PAYMENT_REQUIRED: {
    en: 'Pick at least one payment method.',
    ar: 'اختر طريقة دفع واحدة على الأقل.',
  },
  PAYMENT_SUM_MISMATCH: {
    en: 'The payments don’t add up to the invoice total. Adjust them and try again.',
    ar: 'مجموع المدفوعات لا يساوي إجمالي الفاتورة. عدّلها ثم أعد المحاولة.',
  },
  CASH_TENDERED_TOO_LOW: {
    en: 'The cash received is less than the amount applied.',
    ar: 'النقد المستلم أقل من المبلغ المطبق.',
  },
  CUSTOMER_REQUIRED_FOR_ON_ACCOUNT: {
    en: 'Attach a customer to the appointment before saving it unpaid or on account.',
    ar: 'أضف عميلاً إلى الموعد قبل حفظه كغير مدفوع أو على الحساب.',
  },
  TOTALS_MISMATCH: {
    en: 'The totals changed while you were paying. Reopen the appointment and try again.',
    ar: 'تغيّرت الإجماليات أثناء الدفع. أعد فتح الموعد ثم حاول مرة أخرى.',
  },
  INVALID_PAYMENT_TYPE: {
    en: 'That payment method isn’t available for sales.',
    ar: 'طريقة الدفع هذه غير متاحة للمبيعات.',
  },
  SHIFT_OPEN_FAILED: {
    en: 'Could not open a Cash Drawer shift for this sale. Contact your administrator.',
    ar: 'تعذّر فتح وردية درج النقديه لهذه الفاتورة. يُرجى مراجعة مسؤول النظام.',
  },
  BRANCH_NOT_ALLOWED: {
    en: 'You don’t have access to that branch.',
    ar: 'ليس لديك صلاحية الوصول إلى هذا الفرع.',
  },
};

/** Sign-in / session rejections — the shared cashier auth endpoints. */
const AUTH_ERRORS: Record<string, Entry> = {
  INVALID_CREDENTIALS: {
    en: 'Wrong username or password.',
    ar: 'اسم المستخدم أو كلمة المرور غير صحيحة.',
  },
  USER_NOT_FOUND: {
    en: 'Wrong username or password.',
    ar: 'اسم المستخدم أو كلمة المرور غير صحيحة.',
  },
  PASSWORD_NOT_SET: {
    en: 'This account has no password set. Contact your administrator.',
    ar: 'لم يتم تعيين كلمة مرور لهذا الحساب. يُرجى مراجعة مسؤول النظام.',
  },
  PASSWORD_EXPIRED: {
    en: 'Your password has expired. Contact your administrator.',
    ar: 'انتهت صلاحية كلمة المرور. يُرجى مراجعة مسؤول النظام.',
  },
  ACCOUNT_LOCKED: {
    en: 'This account is locked. Contact your administrator.',
    ar: 'هذا الحساب مقفل. يُرجى مراجعة مسؤول النظام.',
  },
  ACCOUNT_INACTIVE: {
    en: 'This account is inactive. Contact your administrator.',
    ar: 'هذا الحساب غير مُفعَّل. يُرجى مراجعة مسؤول النظام.',
  },
  ACCOUNT_DELETED: {
    en: 'This account no longer exists.',
    ar: 'هذا الحساب لم يعد موجودًا.',
  },
  NO_POS_ACCESS: {
    en: 'This user is not allowed to use the POS.',
    ar: 'هذا المستخدم غير مصرَّح له باستخدام نقطة البيع.',
  },
  ROLE_NOT_ALLOWED: {
    en: 'This user’s role cannot sign in here.',
    ar: 'صلاحية هذا المستخدم لا تسمح بتسجيل الدخول من هنا.',
  },
  NO_TENANT_SLUG: {
    en: 'Enter your workspace name.',
    ar: 'يُرجى إدخال اسم مساحة العمل.',
  },
  INVALID_TENANT_SLUG: {
    en: 'That workspace name isn’t valid.',
    ar: 'اسم مساحة العمل غير صالح.',
  },
  TENANT_NOT_FOUND: {
    en: 'Unknown workspace.',
    ar: 'مساحة العمل غير موجودة.',
  },
  TENANT_SUSPENDED: {
    en: 'This workspace is suspended.',
    ar: 'مساحة العمل موقوفة.',
  },
  TENANT_UNAVAILABLE: {
    en: 'The workspace is temporarily unavailable.',
    ar: 'مساحة العمل غير متاحة مؤقتًا.',
  },
  TENANT_CONFIG_NOT_PROVISIONED: {
    en: 'This workspace isn’t set up yet.',
    ar: 'لم يتم إعداد مساحة العمل بعد.',
  },
  NO_SUBSIDIARIES: {
    en: 'This user has no subsidiaries assigned.',
    ar: 'لا توجد شركات مُخصَّصة لهذا المستخدم.',
  },
  NO_BRANCHES: {
    en: 'This subsidiary has no branches assigned.',
    ar: 'لا توجد فروع مُخصَّصة لهذه الشركة.',
  },
  NO_DRAWERS: {
    en: 'This branch has no cash drawer configured.',
    ar: 'لا يوجد درج نقدية مُعَد لهذا الفرع.',
  },
  DRAWER_INACTIVE: {
    en: 'This cash drawer is inactive. Contact your administrator.',
    ar: 'درج النقدية هذا غير نشط. تواصل مع المسؤول.',
  },
  NO_CASH_DRAWER: {
    en: 'This branch has no cash drawer configured.',
    ar: 'لا يوجد درج نقدية مُعَد لهذا الفرع.',
  },
  SUBSIDIARY_NOT_FOUND: {
    en: 'That subsidiary no longer exists.',
    ar: 'الشركة المحددة لم تعد موجودة.',
  },
  BRANCH_NOT_FOUND: {
    en: 'That branch no longer exists.',
    ar: 'الفرع المحدد لم يعد موجودًا.',
  },
  FORBIDDEN_SUBSIDIARY: {
    en: 'You don’t have access to that subsidiary.',
    ar: 'ليس لديك صلاحية الوصول إلى هذه الشركة.',
  },
  FORBIDDEN_BRANCH: {
    en: 'You don’t have access to that branch.',
    ar: 'ليس لديك صلاحية الوصول إلى هذا الفرع.',
  },
  FORBIDDEN_DRAWER: {
    en: 'You don’t have access to that Cash Drawer.',
    ar: 'ليس لديك صلاحية الوصول إلى درج النقديه هذا.',
  },
  INVALID_TOKEN: {
    en: 'Your session expired — please sign in again.',
    ar: 'انتهت صلاحية الجلسة — يُرجى تسجيل الدخول مرة أخرى.',
  },
};

/** Last-resort message when nothing more specific is known. */
const UNKNOWN_MSG: Msg = {
  en: 'Something went wrong. Please try again.',
  ar: 'حدث خطأ ما. يُرجى المحاولة مرة أخرى.',
};

/** Transport / generic failures shared by every screen. */
const GENERIC_ERRORS: Record<string, Entry> = {
  VALIDATION_ERROR: {
    en: 'Some of the details are incomplete or invalid. Please review the form.',
    ar: 'بعض البيانات ناقصة أو غير صحيحة. يُرجى مراجعة النموذج.',
  },
  INVALID_REQUEST: {
    en: 'Some of the details are incomplete or invalid. Please review the form.',
    ar: 'بعض البيانات ناقصة أو غير صحيحة. يُرجى مراجعة النموذج.',
  },
  BAD_REQUEST: {
    en: 'The request couldn’t be completed.',
    ar: 'تعذّر إتمام الطلب.',
  },
  FORBIDDEN: {
    en: 'You don’t have permission to do that.',
    ar: 'ليس لديك صلاحية للقيام بهذا الإجراء.',
  },
  INTERNAL_ERROR: {
    en: 'Something went wrong on the server. Please try again.',
    ar: 'حدث خطأ في الخادم. يُرجى المحاولة مرة أخرى.',
  },
  DB_UNAVAILABLE: {
    en: 'The database is temporarily unavailable. Please try again.',
    ar: 'قاعدة البيانات غير متاحة مؤقتًا. يُرجى المحاولة مرة أخرى.',
  },
  NETWORK: {
    en: 'No connection to the server. Check your network and try again.',
    ar: 'لا يوجد اتصال بالخادم. تحقّق من الشبكة ثم أعد المحاولة.',
  },
  REQUEST_FAILED: {
    en: 'The request failed. Please try again.',
    ar: 'فشل الطلب. يُرجى المحاولة مرة أخرى.',
  },
  UNKNOWN: UNKNOWN_MSG,
};

const ALL_ERRORS: Record<string, Entry> = {
  ...GENERIC_ERRORS,
  ...AUTH_ERRORS,
  ...ORDER_ERRORS,
  ...APPOINTMENT_ERRORS,
};

const looksLikeCode = (s: string): boolean => /^[A-Z][A-Z0-9_]{2,}$/.test(s);

interface Extracted {
  code: string | null;
  params: Params;
  /** The server's English prose, when it sent any. */
  serverMessage: string | null;
}

/** Pull a machine code + interpolation params out of whatever the caller threw. */
export function extractError(e: unknown): Extracted {
  const ax = e as {
    response?: { data?: { error?: string; code?: string; message?: string; params?: Params } };
    code?: string;
    message?: string;
  };
  const data = ax?.response?.data;

  // `code` is the global handler's slot; `error` is the appointments routes' slot
  // (which holds prose in the global shape, hence the CODE-shaped guard).
  const fromCode = data?.code && looksLikeCode(data.code) ? data.code : null;
  const fromError = data?.error && looksLikeCode(data.error) ? data.error : null;

  // No response at all → the request never landed (offline, DNS, CORS, abort).
  const transport = !ax?.response && (ax?.code === 'ERR_NETWORK' || ax?.code === 'ECONNABORTED')
    ? 'NETWORK'
    : null;

  return {
    code: fromCode ?? fromError ?? transport,
    params: data?.params ?? {},
    serverMessage: data?.message ?? (data?.error && !looksLikeCode(data.error) ? data.error : null),
  };
}

/** Resolve a code (+params) to a bilingual message, or null when unmapped. */
export function messageForCode(code: string | null, params: Params = {}): Msg | null {
  if (!code) return null;
  const entry = ALL_ERRORS[code];
  if (!entry) return null;
  return typeof entry === 'function' ? entry(params) : entry;
}

/** Bilingual text for an auth/session error code — used by the login screen. */
export function authErrorMessage(code: string): Msg {
  return messageForCode(code) ?? UNKNOWN_MSG;
}

/**
 * Translate a thrown request error for display. Falls back to a generic
 * sentence in the active language; the raw code (or the server's own prose in
 * English) is appended so an unmapped failure is still diagnosable.
 */
export function useErrMsg(): (e: unknown, fallback?: Msg) => string {
  const { t, lang } = useLang();
  return useCallback(
    (e: unknown, fallback?: Msg): string => {
      const { code, params, serverMessage } = extractError(e);
      const mapped = messageForCode(code, params);
      if (mapped) return t(mapped);

      const generic = t(fallback ?? UNKNOWN_MSG);
      // English readers get the server's wording verbatim when it sent any;
      // Arabic readers get the generic sentence plus the code for support.
      if (lang === 'en' && serverMessage) return serverMessage;
      const detail = code ?? (lang === 'en' ? serverMessage : null);
      return detail ? `${generic} (${detail})` : generic;
    },
    [t, lang],
  );
}
